import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../services/cartService";
import { getMyReferralInfoAPI } from "../services/referralService";

const BASE_URL = process.env.REACT_APP_API_URL ; 

/* ─────────────────────────────────────────────
   API HELPERS
───────────────────────────────────────────── */
const getMe = async () => {
  const res = await fetchWithAuth(`${BASE_URL}/auth/me`);
  return res.json();
};

const updateProfile = async (data) => {
  const res = await fetchWithAuth(`${BASE_URL}/users/profile`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
};

const changePassword = async (data) => {
  const res = await fetchWithAuth(`${BASE_URL}/users/change-password`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
};

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function Toast({ msg, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className={`toast toast--${type}`}>
      <span>{type === "success" ? "✓" : "✕"}</span> {msg}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL
───────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <h3 className="modal__title">{title}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function UserProfile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);

  const navigate = useNavigate();

  // Edit modal state
  const [editModal, setEditModal] = useState(null); // "profile" | "password" | null

  // Profile form
  const [profileForm, setProfileForm] = useState({ name: "", phone: "", email: "" });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});

  // Password form
  const [passForm, setPassForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passLoading, setPassLoading] = useState(false);
  const [passErrors, setPassErrors] = useState({});
  const [showPass, setShowPass] = useState({ current: false, new: false, confirm: false });

  /* ── Load user from backend ── */
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    console.log("token:", localStorage.getItem("accessToken"));
    if (!token) { navigate("/login"); return; }

    (async () => {
      try {
        const result = await getMe();
        if (result?.success) {
          const u = result.data?.user || result.data;
          setUser(u);
          localStorage.setItem("user", JSON.stringify(u));
        } else {
          navigate("/login");
        }
      } catch {
        // fallback to localStorage
        const stored = localStorage.getItem("user");
        if (stored && stored !== "undefined") {
          try { setUser(JSON.parse(stored)); } catch { navigate("/login"); }
        } else {
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // Add to UserProfile after loading user:

  useEffect(() => {
    getMyReferralInfoAPI().then(r => { if (r?.success) setReferralInfo(r.data); });
  }, []);

  const showToast = (msg, type = "success") => setToast({ msg, type });

  /* ── Open profile edit modal ── */
  const openProfileModal = () => {
    setProfileForm({ name: user.name || "", phone: user.phone || "", email: user.email || "" });
    setProfileErrors({});
    setEditModal("profile");
  };

  /* ── Open password modal ── */
  const openPasswordModal = () => {
    setPassForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPassErrors({});
    setEditModal("password");
  };

  /* ── Validate profile form ── */
  const validateProfile = (f) => {
    const e = {};
    if (!f.name.trim() || f.name.trim().length < 3) e.name = "Name must be at least 3 characters.";
    if (!f.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email.";
    if (f.phone && !/^[6-9]\d{9}$/.test(f.phone.trim())) e.phone = "Enter a valid 10-digit mobile number.";
    return e;
  };

  /* ── Submit profile update ── */
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const errs = validateProfile(profileForm);
    if (Object.keys(errs).length) { setProfileErrors(errs); return; }

    setProfileLoading(true);
    try {
      const result = await updateProfile({
        name: profileForm.name.trim(),
        email: profileForm.email.trim(),
        phone: profileForm.phone.trim(),
      });

      if (result?.success) {
        const updated = result.data?.user || result.data;
        setUser(updated);
        localStorage.setItem("user", JSON.stringify(updated));
        setEditModal(null);
        showToast("Profile updated successfully!");
      } else {
        showToast(result?.message || "Update failed.", "error");
      }
    } catch {
      showToast("Something went wrong.", "error");
    } finally {
      setProfileLoading(false);
    }
  };

  /* ── Validate password form ── */
  const validatePass = (f) => {
    const e = {};
    if (!f.currentPassword) e.currentPassword = "Current password is required.";
    if (!f.newPassword || f.newPassword.length < 6) e.newPassword = "New password must be at least 6 characters.";
    if (f.newPassword !== f.confirmPassword) e.confirmPassword = "Passwords do not match.";
    return e;
  };

  /* ── Submit password change ── */
  const handlePassSubmit = async (e) => {
    e.preventDefault();
    const errs = validatePass(passForm);
    if (Object.keys(errs).length) { setPassErrors(errs); return; }

    setPassLoading(true);
    try {
      const result = await changePassword({
        currentPassword: passForm.currentPassword,
        newPassword: passForm.newPassword,
      });

      if (result?.success) {
        setEditModal(null);
        showToast("Password changed successfully!");
      } else {
        showToast(result?.message || "Password change failed.", "error");
      }
    } catch {
      showToast("Something went wrong.", "error");
    } finally {
      setPassLoading(false);
    }
  };

  /* ── Logout ── */
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  if (loading) return (
    <>
      <style>{css}</style>
      <div className="profile-page">
        <div className="profile-loading"><div className="profile-spinner" /><p>Loading your profile…</p></div>
      </div>
    </>
  );

  if (!user) return null;

  const initials = user.name
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : user.email?.[0]?.toUpperCase() || "U";

  const displayName = user.name || "Wellness Member";



  return (
    <>
      <style>{css}</style>

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div className="profile-page">
        <div className="profile-container">

          {/* Header */}
          <div className="profile-header">
            <Link to="/" className="profile-back">←</Link>
            <h1 className="profile-header__title">My Profile</h1>
          </div>

          {/* Hero */}
          <div className="profile-hero">
            <div className="profile-avatar">{initials}</div>
            <h2 className="profile-name">{displayName}</h2>
            <p className="profile-email">{user.email}</p>
            <div className="profile-badge"><span>🌿</span> Sanjivani Wellness Member</div>
          </div>

          {/* Info Cards */}
          <div className="profile-section">
            <div className="profile-section__header">
              <h3 className="profile-section__title">Personal Information</h3>
              <button className="edit-btn" onClick={openProfileModal}>✏️ Edit</button>
            </div>
            <div className="profile-grid">
              <div className="info-card">
                <div className="info-card__label">Full Name</div>
                <div className="info-card__value">{user.name || <span className="info-card__value--muted">Not set</span>}</div>
              </div>
              <div className="info-card">
                <div className="info-card__label">Phone</div>
                <div className="info-card__value">{user.phone ? `+91 ${user.phone}` : <span className="info-card__value--muted">Not set</span>}</div>
              </div>
              <div className="info-card info-card--full">
                <div className="info-card__label">Email Address</div>
                <div className="info-card__value">{user.email}</div>
              </div>
              <div className="info-card">
                <div className="info-card__label">Account Role</div>
                <div className="info-card__value" style={{ textTransform: "capitalize" }}>{user.role || "user"}</div>
              </div>
              <div className="info-card">
                <div className="info-card__label">Member Since</div>
                <div className="info-card__value">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="profile-section">
            <div className="profile-section__header">
              <h3 className="profile-section__title">Security</h3>
              <button className="edit-btn" onClick={openPasswordModal}>🔒 Change</button>
            </div>
            <div className="profile-grid">
              <div className="info-card info-card--full">
                <div className="info-card__label">Password</div>
                <div className="info-card__value">••••••••••••</div>
              </div>
            </div>
          </div>

          {/* Dosha decorative */}
          {/* <div className="dosha-card">
            <h3 className="dosha-card__title">Your Prakriti Balance</h3>
            <div className="dosha-row">
              {[
                { name: "Vata", pct: 45, color: "#74c69d" },
                { name: "Pitta", pct: 30, color: "#f4a261" },
                { name: "Kapha", pct: 25, color: "#a8dadc" },
              ].map(({ name, pct, color }) => (
                <div className="dosha-item" key={name}>
                  <span className="dosha-item__name">{name}</span>
                  <div className="dosha-item__bar-bg">
                    <div className="dosha-item__bar-fill" style={{ width: `${pct}%`, background: color }} />
                  </div>
                  <span className="dosha-item__pct">{pct}%</span>
                </div>
              ))}
            </div>
          </div> */}

          {/* Actions */}
          <div className="profile-actions">
            <Link to="/orders" className="btn-action">
              <div className="btn-action__left">
                <div className="btn-action__icon btn-action__icon--green">🛒</div>
                <div>
                  <div className="btn-action__text">My Orders</div>
                  <div className="btn-action__sub">View your order history</div>
                </div>
              </div>
              <span className="btn-action__arrow">›</span>
            </Link>

            <Link to="/cart" className="btn-action">
              <div className="btn-action__left">
                <div className="btn-action__icon btn-action__icon--amber">🛍️</div>
                <div>
                  <div className="btn-action__text">My Cart</div>
                  <div className="btn-action__sub">Continue shopping</div>
                </div>
              </div>
              <span className="btn-action__arrow">›</span>
            </Link>

            <button className="btn-action btn-logout" onClick={handleLogout}>
              <div className="btn-action__left">
                <div className="btn-action__icon btn-action__icon--red">🚪</div>
                <div>
                  <div className="btn-action__text">Logout</div>
                  <div className="btn-action__sub">Sign out of your account</div>
                </div>
              </div>
              <span className="btn-action__arrow" style={{ color: "#e57373" }}>›</span>
            </button>

          </div>

          <div className="profile-section m-5">
            <div className="profile-section__header">
              <h3 className="profile-section__title">Referral & Rewards</h3>
            </div>

            <div className="profile-grid">

              {/* Referral Code */}
              <div className="info-card info-card--full referral-card">
                <div className="info-card__label">Your Referral Code</div>

                <div className="referral-code-wrap">
                  <span className="referral-code">
                    {referralInfo?.referralCode || "—"}
                  </span>

                  <button
                    className="copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(referralInfo?.referralCode);
                      showToast("Referral code copied!");
                    }}
                  >
                    Copy
                  </button>
                </div>

                <div className="referral-desc">
                  🎉 Share this code with friends. They get 10% OFF and you earn reward coupons.
                </div>
              </div>

              {/* Loyalty Progress */}
              {/* <div className="info-card info-card--full loyalty-card">
                <div className="info-card__label">Loyalty Progress</div>

                <div className="loyalty-header">
                  <span>Items Purchased</span>
                  <span className="loyalty-count">
                    {referralInfo?.purchaseCounter || 0}/4
                  </span>
                </div>

                <div className="loyalty-progress">
                  <div
                    className="loyalty-progress-fill"
                    style={{
                      width: `${Math.min(
                        ((referralInfo?.purchaseCounter || 0) / 4) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>

                <div className="loyalty-message">
                  {referralInfo?.loyaltyDiscountUnlocked
                    ? "🎁 Congratulations! Your 50% discount is unlocked."
                    : `Buy ${4 - (referralInfo?.purchaseCounter || 0)
                    } more item${4 - (referralInfo?.purchaseCounter || 0) !== 1 ? "s" : ""
                    } to unlock 50% OFF`}
                </div>
              </div> */}


            </div>
          </div>

        </div>
      </div>

      {/* ── EDIT PROFILE MODAL ── */}
      {editModal === "profile" && (
        <Modal title="Edit Profile" onClose={() => setEditModal(null)}>
          <form onSubmit={handleProfileSubmit} className="modal-form" noValidate>
            <div className="mf-group">
              <label className="mf-label">Full Name <span className="mf-req">*</span></label>
              <input
                className={`mf-input ${profileErrors.name ? "mf-input--error" : ""}`}
                value={profileForm.name}
                onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Priya Sharma"
              />
              {profileErrors.name && <p className="mf-error">{profileErrors.name}</p>}
            </div>

            <div className="mf-group">
              <label className="mf-label">Email Address <span className="mf-req">*</span></label>
              <input
                className={`mf-input ${profileErrors.email ? "mf-input--error" : ""}`}
                type="email"
                value={profileForm.email}
                onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                placeholder="priya@example.com"
              />
              {profileErrors.email && <p className="mf-error">{profileErrors.email}</p>}
            </div>

            <div className="mf-group">
              <label className="mf-label">Mobile Number</label>
              <div className="mf-prefix-wrap">
                <span className="mf-prefix">+91</span>
                <input
                  className={`mf-input mf-input--prefix ${profileErrors.phone ? "mf-input--error" : ""}`}
                  type="tel"
                  value={profileForm.phone}
                  onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="98765 43210"
                  maxLength={10}
                />
              </div>
              {profileErrors.phone && <p className="mf-error">{profileErrors.phone}</p>}
            </div>

            <div className="modal-actions">
              <button type="button" className="modal-btn modal-btn--outline" onClick={() => setEditModal(null)}>Cancel</button>
              <button type="submit" className="modal-btn modal-btn--primary" disabled={profileLoading}>
                {profileLoading ? <span className="btn-spinner" /> : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── CHANGE PASSWORD MODAL ── */}
      {editModal === "password" && (
        <Modal title="Change Password" onClose={() => setEditModal(null)}>
          <form onSubmit={handlePassSubmit} className="modal-form" noValidate>
            {["currentPassword", "newPassword", "confirmPassword"].map((field) => {
              const labels = { currentPassword: "Current Password", newPassword: "New Password", confirmPassword: "Confirm New Password" };
              const showKey = field === "currentPassword" ? "current" : field === "newPassword" ? "new" : "confirm";
              return (
                <div className="mf-group" key={field}>
                  <label className="mf-label">{labels[field]} <span className="mf-req">*</span></label>
                  <div className="mf-pass-wrap">
                    <input
                      className={`mf-input mf-input--pass ${passErrors[field] ? "mf-input--error" : ""}`}
                      type={showPass[showKey] ? "text" : "password"}
                      value={passForm[field]}
                      onChange={e => setPassForm(p => ({ ...p, [field]: e.target.value }))}
                      placeholder="••••••••"
                    />
                    <button type="button" className="mf-eye" onClick={() => setShowPass(p => ({ ...p, [showKey]: !p[showKey] }))}>
                      {showPass[showKey] ? "🙈" : "👁️"}
                    </button>
                  </div>
                  {passErrors[field] && <p className="mf-error">{passErrors[field]}</p>}
                </div>
              );
            })}

            <div className="modal-actions">
              <button type="button" className="modal-btn modal-btn--outline" onClick={() => setEditModal(null)}>Cancel</button>
              <button type="submit" className="modal-btn modal-btn--primary" disabled={passLoading}>
                {passLoading ? <span className="btn-spinner" /> : "Update Password"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=Jost:wght@300;400;500;600&display=swap');

:root {
  --c-primary: #2d6a4f;
  --c-primary-dark: #1b4332;
  --c-primary-light: #40916c;
  --c-accent: #d4a017;
  --c-bg: #f9f6f0;
  --c-surface: #ffffff;
  --c-text: #1c2b22;
  --c-muted: #5a7262;
  --c-border: #d4e6d5;
  --c-error: #c0392b;
  --c-error-bg: #fdf2f2;
  --f-display: 'Cormorant Garamond', Georgia, serif;
  --f-body: 'Jost', sans-serif;
  --radius: 14px;
  --radius-sm: 8px;
  --ease: cubic-bezier(.4,0,.2,1);
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: var(--f-body); background: var(--c-bg); color: var(--c-text); }
a { text-decoration: none; color: inherit; }

/* ── TOAST ── */
.toast {
  position: fixed; top: 24px; right: 24px; z-index: 9999;
  padding: 14px 20px; border-radius: var(--radius-sm);
  font-family: var(--f-body); font-size: .88rem; font-weight: 600;
  display: flex; align-items: center; gap: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,.15);
  animation: slideIn .3s var(--ease);
}
.toast--success { background: var(--c-primary); color: #fff; }
.toast--error { background: var(--c-error); color: #fff; }
@keyframes slideIn { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:none} }

/* ── PAGE ── */
.profile-page { min-height: 100vh; background: var(--c-bg); padding: 0 0 60px; }
.profile-container { max-width: 1120px; margin: 0 auto; padding: 0 20px; }

.profile-loading {
  min-height: 60vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 16px; color: var(--c-muted);
}
.profile-spinner {
  width: 36px; height: 36px; border-radius: 50%;
  border: 3px solid var(--c-border); border-top-color: var(--c-primary);
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── HEADER ── */
.profile-header {
  display: flex; align-items: center; gap: 16px;
  padding: 24px 0 20px;
}
.profile-back {
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--c-surface); border: 1px solid var(--c-border);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; color: var(--c-primary); flex-shrink: 0;
  transition: background .2s;
}
.profile-back:hover { background: var(--c-border); }
.profile-header__title {
  font-family: var(--f-display); font-size: 1.8rem;
  font-weight: 600; color: var(--c-primary-dark);
}

/* ── HERO ── */
.profile-hero {
  background: linear-gradient(135deg, var(--c-primary-dark), #1e4d35);
  border-radius: var(--radius); padding: 36px 24px 28px;
  text-align: center; margin-bottom: 24px; position: relative; overflow: hidden;
  animation: fadeUp .5s var(--ease) both;
}
.profile-hero::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(ellipse 80% 100% at 80% 20%, rgba(64,145,108,.25), transparent);
}
@keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
.profile-avatar {
  width: 72px; height: 72px; border-radius: 50%;
  background: rgba(255,255,255,.15); border: 2.5px solid rgba(255,255,255,.4);
  display: flex; align-items: center; justify-content: center;
  font-family: var(--f-display); font-size: 1.8rem; font-weight: 600;
  color: #fff; margin: 0 auto 14px; position: relative;
}
.profile-name { font-family: var(--f-display); font-size: 1.6rem; font-weight: 600; color: #fff; margin-bottom: 4px; position: relative; }
.profile-email { font-size: .82rem; color: rgba(255,255,255,.6); font-weight: 300; margin-bottom: 14px; position: relative; }
.profile-badge {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2);
  color: rgba(255,255,255,.85); font-size: .72rem; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase;
  padding: 5px 14px; border-radius: 100px; position: relative;
}

/* ── SECTIONS ── */
.profile-section {
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); padding: 20px 22px; margin-bottom: 16px;
  animation: fadeUp .5s var(--ease) .08s both;
}
.profile-section__header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid var(--c-border);
}
.profile-section__title {
  font-family: var(--f-display); font-size: 1.1rem;
  font-weight: 600; color: var(--c-primary-dark);
}
.edit-btn {
  font-family: var(--f-body); font-size: .76rem; font-weight: 600;
  letter-spacing: .05em; padding: 6px 14px; border-radius: 100px;
  border: 1.5px solid var(--c-border); background: var(--c-bg);
  color: var(--c-primary); cursor: pointer;
  transition: all .22s var(--ease);
}
.edit-btn:hover { border-color: var(--c-primary); background: rgba(45,106,79,.06); }

/* ── INFO GRID ── */
.profile-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.info-card {
  background: var(--c-bg); border: 1px solid var(--c-border);
  border-radius: var(--radius-sm); padding: 14px 16px;
}
.info-card--full { grid-column: 1 / -1; }
.info-card__label { font-size: .68rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--c-muted); margin-bottom: 5px; }
.info-card__value { font-size: .9rem; font-weight: 500; color: var(--c-text); }
.info-card__value--muted { color: var(--c-muted); font-weight: 300; font-style: italic; }

/* ── DOSHA ── */
.dosha-card {
  background: linear-gradient(135deg, rgba(45,106,79,.04), var(--c-surface));
  border: 1px solid var(--c-border); border-radius: var(--radius);
  padding: 22px; margin-bottom: 16px;
  animation: fadeUp .5s var(--ease) .16s both;
}
.dosha-card__title { font-family: var(--f-display); font-size: 1.1rem; font-weight: 600; color: var(--c-primary-dark); margin-bottom: 18px; }
.dosha-row { display: flex; flex-direction: column; gap: 12px; }
.dosha-item { display: flex; align-items: center; gap: 12px; }
.dosha-item__name { font-size: .8rem; font-weight: 600; color: var(--c-muted); width: 44px; flex-shrink: 0; }
.dosha-item__bar-bg { flex: 1; height: 8px; border-radius: 100px; background: var(--c-border); overflow: hidden; }
.dosha-item__bar-fill { height: 100%; border-radius: 100px; transition: width .8s var(--ease); }
.dosha-item__pct { font-size: .76rem; font-weight: 600; color: var(--c-muted); width: 36px; text-align: right; flex-shrink: 0; }

/* ── ACTIONS ── */
.profile-actions { display: flex;  gap: 10px; animation: fadeUp .5s var(--ease) .22s both; }
.btn-action {
  display: flex; align-items: center; justify-content: space-between;
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); padding: 16px 20px; cursor: pointer;
  font-family: var(--f-body); width: 100%;
  transition: border-color .22s, box-shadow .22s, transform .22s;
  text-decoration: none; color: inherit;
}
.btn-action:hover { border-color: var(--c-primary-light); box-shadow: 0 4px 16px rgba(45,106,79,.1); transform: translateY(-1px); }
.btn-action__left { display: flex; align-items: center; gap: 14px; }
.btn-action__icon {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0;
}
.btn-action__icon--green { background: rgba(45,106,79,.1); }
.btn-action__icon--amber { background: rgba(212,160,23,.1); }
.btn-action__icon--red { background: rgba(192,57,43,.1); }
.btn-action__text { font-size: .9rem; font-weight: 600; color: var(--c-text); margin-bottom: 2px; }
.btn-action__sub { font-size: .74rem; color: var(--c-muted); font-weight: 300; }
.btn-action__arrow { font-size: 1.3rem; color: var(--c-muted); }
.btn-logout { border: none; }
.btn-logout:hover { border-color: #e57373; box-shadow: 0 4px 16px rgba(192,57,43,.1); }

/* ── MODAL ── */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(27,67,50,.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
  animation: fadeOverlay .2s var(--ease);
}
@keyframes fadeOverlay { from{opacity:0} to{opacity:1} }
.modal {
  background: var(--c-surface); border-radius: var(--radius);
  width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto;
  box-shadow: 0 24px 64px rgba(27,67,50,.25);
  animation: slideUp .3s var(--ease);
}
@keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:none} }
.modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 16px; border-bottom: 1px solid var(--c-border);
}
.modal__title { font-family: var(--f-display); font-size: 1.3rem; font-weight: 600; color: var(--c-primary-dark); }
.modal__close {
  width: 32px; height: 32px; border-radius: 50%; border: 1px solid var(--c-border);
  background: var(--c-bg); cursor: pointer; font-size: .8rem;
  display: flex; align-items: center; justify-content: center;
  color: var(--c-muted); transition: all .2s;
}
.modal__close:hover { background: var(--c-border); color: var(--c-text); }

/* ── MODAL FORM ── */
.modal-form { padding: 20px 24px 24px; display: flex; flex-direction: column; gap: 16px; }
.mf-group { display: flex; flex-direction: column; gap: 6px; }
.mf-label { font-size: .78rem; font-weight: 600; letter-spacing: .04em; color: var(--c-text); }
.mf-req { color: var(--c-error); margin-left: 2px; }
.mf-input {
  font-family: var(--f-body); font-size: .9rem; color: var(--c-text);
  padding: 11px 14px; border: 1.5px solid var(--c-border);
  border-radius: var(--radius-sm); background: var(--c-bg);
  outline: none; width: 100%;
  transition: border-color .22s, box-shadow .22s, background .22s;
}
.mf-input:focus { border-color: var(--c-primary-light); box-shadow: 0 0 0 3px rgba(64,145,108,.13); background: var(--c-surface); }
.mf-input--error { border-color: var(--c-error); background: var(--c-error-bg); }
.mf-error { font-size: .72rem; color: var(--c-error); }
.mf-prefix-wrap { position: relative; display: flex; align-items: center; }
.mf-prefix { position: absolute; left: 13px; font-size: .85rem; font-weight: 600; color: var(--c-muted); pointer-events: none; z-index: 1; }
.mf-input--prefix { padding-left: 44px; }
.mf-pass-wrap { position: relative; }
.mf-input--pass { padding-right: 44px; }
.mf-eye {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; font-size: .9rem; padding: 4px;
}

.modal-actions { display: flex; gap: 10px; margin-top: 4px; }
.modal-btn {
  flex: 1; padding: 12px; border-radius: 100px; border: none;
  font-family: var(--f-body); font-size: .86rem; font-weight: 700;
  letter-spacing: .06em; text-transform: uppercase; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: all .22s var(--ease);
}
.modal-btn--primary { background: var(--c-primary); color: #fff; box-shadow: 0 4px 16px rgba(45,106,79,.28); }
.modal-btn--primary:hover:not(:disabled) { background: var(--c-primary-dark); transform: translateY(-1px); }
.modal-btn--primary:disabled { opacity: .7; cursor: wait; }
.modal-btn--outline { background: transparent; color: var(--c-muted); border: 1.5px solid var(--c-border); }
.modal-btn--outline:hover { border-color: var(--c-primary); color: var(--c-primary); }

.btn-spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  animation: spin .7s linear infinite;
}

@media (max-width: 480px) {
  .profile-grid { grid-template-columns: 1fr; }
  .info-card--full { grid-column: 1; }
  .profile-hero { padding: 28px 16px 22px; }
  .profile-name { font-size: 1.3rem; }
}

/* ── REFERRAL SECTION ── */

.referral-card {
  background: linear-gradient(
    135deg,
    rgba(45, 106, 79, 0.06),
    rgba(255, 255, 255, 1)
  );
}

.referral-code-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.referral-code {
  font-family: monospace;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--c-primary);
  letter-spacing: 3px;
  background: rgba(45, 106, 79, 0.08);
  padding: 10px 16px;
  border-radius: 10px;
}

.copy-btn {
  border: none;
  background: var(--c-primary);
  color: white;
  padding: 9px 18px;
  border-radius: 100px;
  cursor: pointer;
  font-size: .8rem;
  font-weight: 600;
  transition: all .25s ease;
}

.copy-btn:hover {
  background: var(--c-primary-dark);
  transform: translateY(-2px);
}

.referral-desc {
  margin-top: 12px;
  color: var(--c-muted);
  font-size: .82rem;
  line-height: 1.5;
}

/* ── LOYALTY CARD ── */

.loyalty-card {
  background: linear-gradient(
    135deg,
    rgba(212,160,23,.08),
    rgba(255,255,255,1)
  );
}

.loyalty-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: .9rem;
  font-weight: 600;
}

.loyalty-count {
  color: var(--c-primary);
  font-weight: 700;
}

.loyalty-progress {
  height: 12px;
  background: var(--c-border);
  border-radius: 100px;
  overflow: hidden;
}

.loyalty-progress-fill {
  height: 100%;
  border-radius: 100px;
  background: linear-gradient(
    90deg,
    var(--c-primary),
    var(--c-primary-light)
  );
  transition: width .8s ease;
}

.loyalty-message {
  margin-top: 12px;
  font-size: .82rem;
  color: var(--c-muted);
  line-height: 1.5;
}
`;